import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Card,
  CardBody,
  CardHeader,
  Collapse,
} from "reactstrap"
import CategoryProductsTable from "./Components/CategoryProductsTable"
import classNames from "classnames"
import { DeleteCategory, AddNewCategory } from "pages/vendor/Categories/Modals"
import { useTranslation } from "react-i18next"
import { Category } from "./Categories"
import {
  getAllProductCategories,
  getProductCategory,
  getCategoryProducts
} from "../../../store/product-categories/actions"
import { toast } from "react-toastify"

export type Product = {
  id: number
  vsid: number
  aR_NAME: string
  eN_NAME: string
  iS_CURRENT: boolean
  iS_ACTIVE: boolean
  price: number
  categorY_ID: number
  supplieR_ID: number
}

const CategoryDetails = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === "ar"

  const handleCategoryDeleted = () => {
    toast.success(t("categories_categoryDeletedSuccessfully") || "Category deleted successfully!")
    navigate("/categories")
  }

  const [checkedProducts, setCheckedProducts] = useState<Product[]>([])
  const [openFirstAccordion, setOpenFirstAccordion] = useState<string>("0")
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false)

  // Get data from Redux
  const { categories, category, categoryProducts, loading } = useSelector(
    (state: any) => state.ProductCategories
  )

  useEffect(() => {
    // Load categories on mount
    dispatch(getAllProductCategories())
  }, [dispatch])

  useEffect(() => {
    if (id) {
      const categoryId = parseInt(id)
      // Load category details
      dispatch(getProductCategory(categoryId))
      // Load category products
      dispatch(getCategoryProducts(categoryId))
    }
  }, [dispatch, id])

  // Set selected category when category data is loaded
  useEffect(() => {
    if (category) {
      setSelectedCategory(category)
    }
  }, [category])

  const products = categoryProducts || []

  const isAllChecked = useMemo(
    () => products.length > 0 && checkedProducts.length === products.length,
    [products, checkedProducts]
  )

  const toggleAllProducts = useCallback(() => {
    setCheckedProducts(isAllChecked ? [] : products)
  }, [isAllChecked, products])

  const togglecheckedProductHandler = useCallback((product: Product) => {
    setCheckedProducts(prev =>
      prev.includes(product)
        ? prev.filter(p => p !== product)
        : [...prev, product]
    )
  }, [])

  const toggleActiveProduct = (product: Product) => {
    console.log(product)
    console.log(checkedProducts)
    // TODO: Implement toggle product active status
  }

  const handleToggle = (event: any) => {
    const id = event.targetValue as string
    setOpenFirstAccordion(openFirstAccordion === id ? "" : id)
  }

  const categoryName = category
    ? (isArabic ? category.aR_NAME : category.eN_NAME)
    : t("categories_categories")

  return (
    <div className="page-content d-flex flex-column">
      <div className="container-fluid p-0">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <Breadcrumbs
            title={t("dashboard_dashboard")}
            breadcrumbItem={t("categories_categories")}
            subBreadcrumbItem={categoryName}
          />
          <div className="d-flex gap-3 flex-wrap">
            <button
              onClick={() => setShowEditCategoryModal(true)}
              className="btn bg-gradiant-blue border-0 text-white"
            >
              {t("categories_editCategory")}
            </button>
            <button
              onClick={() => {
                setSelectedCategory(category)
                setShowDeleteCategoryModal(true)
              }}
              className="btn btn-outline-danger"
            >
              {t("categories_deleteCategory")}
            </button>
          </div>
        </div>
        <div className="container-fluid">
          <Card className="shadow-md rounded-3 py-2">
            <CardHeader className="bg-transparent d-flex justify-content-between">
              <div>
                <h5 className="card-title m-0">{categoryName}</h5>
                <p className="card-text m-0">
                  {t("common_createdAt")}: {"__"}
                </p>
              </div>
              <div className="d-flex">
                <h5 className="card-title mx-2">
                  {t("categories_productCount")}
                </h5>
                <p className="card-text ">
                  {category?.productCount || 0} {t("categories_products")}
                </p>
              </div>
            </CardHeader>
            <CardBody className="py-1">
              <p className="card-text m-0">
                {"__"}
              </p>
            </CardBody>
          </Card>
          <div className="accordion shadow-md rounded-3 p-2" id="accordion">
            <AccordionItem>
              <h2 className="accordion-header" id="headingOne">
                <button
                  className={classNames("accordion-button", "fw-medium", {
                    collapsed: !openFirstAccordion,
                  })}
                  type="button"
                  onClick={() =>
                    setOpenFirstAccordion(
                      openFirstAccordion === "0" ? "1" : "0"
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  {t("categories_productList")}
                </button>
              </h2>
              <Collapse
                isOpen={openFirstAccordion === "0"}
                className="accordion-collapse"
              >
                <CategoryProductsTable
                  products={products}
                  toggleAllProducts={toggleAllProducts}
                  togglecheckedProductHandler={togglecheckedProductHandler}
                  isAllChecked={isAllChecked}
                  checkedProducts={checkedProducts}
                  toggleActiveProduct={toggleActiveProduct}
                />
              </Collapse>
            </AccordionItem>
          </div>
        </div>
      </div>

      <DeleteCategory
        isOpen={showDeleteCategoryModal}
        toggle={() => {
          setShowDeleteCategoryModal(!showDeleteCategoryModal)
          setSelectedCategory(null)
        }}
        category={selectedCategory}
        allCategories={categories}
        onDeleted={handleCategoryDeleted}
      />

      <AddNewCategory
        isOpen={showEditCategoryModal}
        toggle={() => setShowEditCategoryModal(!showEditCategoryModal)}
        category={category}
      />
    </div>
  )
}

export default CategoryDetails
